import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Spinner } from 'components';
import DateRangePicker from './Daterangepicker';

import { ReactComponent as IconFollowers }    from '../../../assets/icons/Dropdown/Group_2085663789.svg';
import { ReactComponent as IconImpressions }  from '../../../assets/icons/Dropdown/Group_2085663919.svg';
import { ReactComponent as IconReach }        from '../../../assets/icons/Dropdown/Group_2085663920.svg';
import { ReactComponent as IconClicks }       from '../../../assets/icons/Dropdown/Group.svg';
import { ReactComponent as IconProfileVisit } from '../../../assets/icons/Dropdown/Group_2085663922.svg';
import { ReactComponent as IconReferral }     from '../../../assets/icons/Dropdown/Group-1.svg';
import { ReactComponent as IconFavourites }   from '../../../assets/icons/Dropdown/Group-2.svg';
import { ReactComponent as IconLastDays }     from '../../../assets/icons/DatePicker/Group_2085663933.svg';
import { ReactComponent as IconSelectMonth }  from '../../../assets/icons/DatePicker/Group_2085663934.svg';
import { ReactComponent as IconDateRange }    from '../../../assets/icons/DatePicker/Group_2085663935.svg';
import { ReactComponent as IconCalendar }     from '../../../assets/icons/DatePicker/Group_2085663936.svg';

/* ── Generate last N months ───────────────────────────────────── */
const generateRecentMonths = (count = 12) => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key:       `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label:     d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      year:      d.getFullYear(),
      month:     d.getMonth() + 1,
      startDate: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10),
      endDate:   new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10),
    });
  }
  return months;
};

const RECENT_MONTHS = generateRecentMonths(12);

/* ── Date-range helpers ───────────────────────────────────────────
   Used to zero-fill chart days the backend didn't return a row for
   (no activity on a day means no row in `series` — not "no day"). */
const isoDate = d => d.toISOString().slice(0, 10);

// Local-time YYYY-MM-DD (isoDate above is UTC-based, which rolls over to
// "tomorrow" in the evening for anyone west of UTC).
const toLocalIso = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Today's data isn't processed until the end of the day, so the latest
// day we ever show is yesterday.
export const getYesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalIso(d);
};

// 30 complete days ending yesterday.
export const getLast30Range = () => {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { startDate: toLocalIso(start), endDate: toLocalIso(end) };
};

// Inclusive list of 'YYYY-MM-DD' strings between startStr and endStr —
// matches the date format coupon_daily_stats.day is serialized as, so it
// can be used directly as a lookup key against the backend's `series`.
const enumerateDays = (startStr, endStr) => {
  const days = [];
  const cur = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);
  while (cur <= end) {
    days.push(toLocalIso(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
};

// Maps a dropdown metric key to the field name BusinessStatsView returns
// in `series`/`totals`. Kept alongside REDEMPTION_OPTIONS below so both
// stay in sync — anything present here is treated as a "stats metric"
// (backed by the business-stats endpoint); anything absent falls through
// to the original redemptions $ amount rendering.
const STATS_METRIC_MAP = {
  followers: 'followers_total',
  impressions: 'impressions',
  reach: 'unique_impressions',
  clicks: 'clicks',
  favourites: 'favorites',
  redemptions: 'redemptions',
};

/* ── Dropdown options ─────────────────────────────────────────── */
const REDEMPTION_OPTIONS = [
  { key: 'redemptions', label: 'Redemptions', icon: IconClicks },
  { key: 'followers',        label: 'Followers',         icon: IconFollowers    },
  { key: 'impressions',      label: 'Impressions',       icon: IconImpressions  },
  { key: 'reach',            label: 'Reach',             icon: IconReach        },
  { key: 'clicks',           label: 'Clicks',            sublabel: '(Detail Views)', icon: IconClicks },
  // Not yet backed by any endpoint — profile visits have no per-business
  // rollup, and referral signups aren't in the analytics schema at all.
  // Disabled rather than removed so re-enabling is a one-line flip once
  // the backend supports them.
  { key: 'profile_visits',   label: 'Profile Visits',    icon: IconProfileVisit,   disabled: true },
  { key: 'referral_signups', label: 'Referral Sign-ups', icon: IconReferral,       disabled: true },
  { key: 'favourites',       label: 'Favourites',        icon: IconFavourites   },
];

const TIME_OPTIONS = [
  { key: 'last_30_days',  label: 'Last 30 Days',  icon: IconLastDays    },
  { key: 'select_month',  label: 'Select Month',  icon: IconSelectMonth },
  { key: 'date_range',    label: 'Date Range',    icon: IconDateRange   },
];

/* ── Custom tooltip ───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#3B0A1F', borderRadius: '14px', padding: '8px 14px', fontSize: '12px', color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', minWidth: '150px' }}>
        <div style={{ fontWeight: '600', marginBottom: '6px', color: '#fff' }}>{label}</div>
        {payload.map((entry, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '2px' }}>
            <span style={{ color: entry.color, fontWeight: '500' }}>{entry.name}</span>
            <span style={{ fontWeight: '700' }}>{entry.value?.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 5l4 4 4-4" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RedemptionsChart = ({ data, fetching, fetched, onFilterChange, defaultRedemptionKey  }) => {
  const [redemptionOpen, setRedemptionOpen] = useState(false);
  const [timeOpen, setTimeOpen]             = useState(false);
  const [monthOpen, setMonthOpen]           = useState(false);
    const [selectedRedemption, setSelectedRedemption] = useState(
    REDEMPTION_OPTIONS.find(o => o.key === defaultRedemptionKey) || REDEMPTION_OPTIONS[0]
  );
  const [selectedTime, setSelectedTime]     = useState(TIME_OPTIONS[0]);
  const [selectedMonth, setSelectedMonth]   = useState(null);
  const [showPicker, setShowPicker]         = useState(false);
  const [appliedRange, setAppliedRange]     = useState(null);
  const [viewMode, setViewMode]             = useState(null); // null = per-metric default

  const redemptionRef = useRef(null);
  const timeRef       = useRef(null);

  const isStatsMetric = !!STATS_METRIC_MAP[selectedRedemption.key];
  const effectiveMode = viewMode ?? (selectedRedemption.key === 'followers' ? 'cumulative' : 'daily');
  // Reach is unique people per day; summing days would double-count them.
  const canToggle = isStatsMetric && selectedRedemption.key !== 'reach';

  useEffect(() => {
    const handler = e => {
      if (redemptionRef.current && !redemptionRef.current.contains(e.target)) setRedemptionOpen(false);
      if (timeRef.current && !timeRef.current.contains(e.target)) {
        setTimeOpen(false);
        setMonthOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dispatchFilter = ({ redemption = selectedRedemption, time = selectedTime, range = appliedRange, month = selectedMonth }) => {
    let period;
    if (time.key === 'date_range' && range) {
      period = { key: 'date_range', startDate: range.startDate, endDate: range.endDate };
    } else if (time.key === 'select_month' && month) {
      period = { key: 'select_month', startDate: month.startDate, endDate: month.endDate, year: month.year, month: month.month };
    } else {
      period = { key: time.key };
    }
    onFilterChange?.({ type: redemption.key, period });
  };

  const handleTimeOptionClick = (opt) => {
    setSelectedTime(opt);
    if (opt.key === 'last_30_days') {
      setTimeOpen(false);
      setMonthOpen(false);
      setSelectedMonth(null);
      setAppliedRange(null);
      dispatchFilter({ time: opt, range: null, month: null });
    } else if (opt.key === 'select_month') {
      setMonthOpen(o => !o);
    } else if (opt.key === 'date_range') {
      setTimeOpen(false);
      setMonthOpen(false);
      setShowPicker(true);
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setAppliedRange(null);
    setMonthOpen(false);
    setTimeOpen(false);
    dispatchFilter({ time: TIME_OPTIONS.find(o => o.key === 'select_month'), range: null, month });
  };

  // Mirrors Dashboard3's statsParamsForPeriod — the full calendar span for
  // whichever window is currently selected, used below to zero-fill days
  // the backend didn't return a row for (no activity != no day).
  // The end of every window is clamped to yesterday: today's data isn't
  // processed until the end of the day.
  const currentWindowBounds = () => {
    const yesterday = getYesterdayIso();
    const clamp = d => (d > yesterday ? yesterday : d);
    if (selectedTime.key === 'select_month' && selectedMonth) {
      return { startDate: selectedMonth.startDate, endDate: clamp(selectedMonth.endDate) };
    }
    if (selectedTime.key === 'date_range' && appliedRange) {
      return { startDate: appliedRange.startDate, endDate: clamp(appliedRange.endDate) };
    }
    return getLast30Range();
  };

  // Stats-backed metrics (followers/impressions/reach/clicks/favourites)
  // read from `data.series`, one row per day — zero-filled across the full
  // selected window so days with no activity still render (rather than
  // being skipped, which made the chart look like it was missing dates).
  // Followers defaults to a running cumulative total (the seed value backs
  // out `totals.followers_new` from `totals.followers_total` to get the
  // count as of the start of the window); every other metric defaults to
  // daily. The Daily/Cumulative toggle overrides either default.
  const chartData = useMemo(() => {
    if (isStatsMetric) {
      const { startDate, endDate } = currentWindowBounds();
      const allDays = startDate && endDate ? enumerateDays(startDate, endDate) : [];
      const byDay = {};
      (data.series || []).forEach(row => { byDay[row.day] = row; });

      if (selectedRedemption.key === 'followers') {
        if (effectiveMode === 'cumulative') {
          let runningTotal = (data.totals?.followers_total || 0) - (data.totals?.followers_new || 0);
          return allDays.map(day => {
            runningTotal += byDay[day]?.followers_new || 0;
            return { name: day, value: runningTotal };
          });
        }
        return allDays.map(day => ({ name: day, value: byDay[day]?.followers_new || 0 }));
      }
      const field = STATS_METRIC_MAP[selectedRedemption.key];
      let running = 0;
      return allDays.map(day => {
        const v = byDay[day]?.[field] ?? 0;
        running += v;
        return { name: day, value: effectiveMode === 'cumulative' ? running : v };
      });
    }

    // unchanged — redemptions $ amount shape from the dashboards duck
    const raw = data.amount_off_coupons_chart || data.amount_points_received_chart || [];
    return raw.map((item, i) => ({
      name:       item.name || item.label || item.month || `${i + 1}`,
      amountOff:  item.amount_off  ?? item.value  ?? item.amount ?? 0,
      pointsBack: item.points_back ?? item.points ?? 0,
    }));
  }, [data, isStatsMetric, selectedRedemption, selectedTime, selectedMonth, appliedRange, effectiveMode]);

  // Single total pill for stats metrics (there's no "amount off vs points
  // back" split for these); the three-pill redemptions breakdown stays
  // for the original metric.
  const statsTotal = useMemo(() => {
    if (!isStatsMetric) return 0;
    if (selectedRedemption.key === 'followers') return data.totals?.followers_total || 0;
    return data.totals?.[STATS_METRIC_MAP[selectedRedemption.key]] || 0;
  }, [data, isStatsMetric, selectedRedemption]);

  const total = (data.amount_off_coupons_count || 0) + (data.points_back_coupons_count || 0);

  const yAxisConfig = useMemo(() => {
    if (!chartData.length) return { domain: [0, 100], ticks: [0, 20, 40, 60, 80, 100] };
    const maxVal = isStatsMetric
      ? Math.max(...chartData.map(d => d.value))
      : Math.max(...chartData.map(d => Math.max(d.amountOff, d.pointsBack)));
    const rawStep   = ((maxVal || 1) * 1.2) / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const residual  = rawStep / magnitude;
    const niceStep  = Math.max(1, (residual > 5 ? 10 : residual > 2 ? 5 : residual > 1 ? 2 : 1) * magnitude);
    const ticks     = Array.from({ length: 6 }, (_, i) => Math.round(i * niceStep));
    const niceMax   = ticks[ticks.length - 1];
    return { domain: [0, niceMax], ticks };
  }, [chartData, isStatsMetric]);

  const timeButtonLabel = useMemo(() => {
    if (selectedTime.key === 'select_month' && selectedMonth) return selectedMonth.label;
    if (selectedTime.key === 'date_range' && appliedRange) {
      const fmt = d => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return `${fmt(appliedRange.startDate)} – ${fmt(appliedRange.endDate)}`;
    }
    return selectedTime.label;
  }, [selectedTime, selectedMonth, appliedRange]);

  return (
    <>
      {/* ─── ROOT: width:100% so the parent wrapper (flex:1 1 50%) controls the size ─── */}
      <div style={{
        background: '#F2EEEE',
        borderRadius: '24px',
        padding: '20px 16px',
        width: '100%',       /* fills whatever the parent gives it  */
        minWidth: 0,         /* allows shrinking below content size  */
        boxSizing: 'border-box',
        fontFamily: "'Segoe UI', sans-serif",
        position: 'relative',
        zIndex: 10,
      }}>

        {/* ── Top controls ── */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', position: 'relative', zIndex: 10 }}>

          {/* Metric dropdown */}
          <div ref={redemptionRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setRedemptionOpen(o => !o); setTimeOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: 'white', border: '1.5px solid #e8e0e0', borderRadius: '24px', cursor: 'pointer', minWidth: '170px', justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <selectedRedemption.icon width={20} height={20} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>{selectedRedemption.label}</span>
              </div>
              <ChevronDown />
            </button>

            {redemptionOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'white', borderRadius: '16px', boxShadow: '0 8px 28px rgba(0,0,0,0.12)', minWidth: '220px', zIndex: 50, padding: '6px 0', border: '1px solid #f0e8e8' }}>
                {REDEMPTION_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.key}
                    disabled={opt.disabled}
                    onClick={() => { if (opt.disabled) return; setSelectedRedemption(opt); setViewMode(null); setRedemptionOpen(false); dispatchFilter({ redemption: opt }); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '11px 18px',
                      background: selectedRedemption.key === opt.key ? '#fdf5f5' : 'transparent',
                      border: 'none', cursor: opt.disabled ? 'not-allowed' : 'pointer', textAlign: 'left',
                      opacity: opt.disabled ? 0.4 : 1,
                      borderBottom: i < REDEMPTION_OPTIONS.length - 1 ? '1px solid #f5f0f0' : 'none',
                    }}
                  >
                    <opt.icon width={20} height={20} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>
                      {opt.label}
                      {opt.sublabel && <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '4px' }}>{opt.sublabel}</span>}
                      {opt.disabled && <span style={{ fontSize: '11px', color: '#bbb', marginLeft: '4px' }}>(coming soon)</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Time dropdown */}
          <div ref={timeRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setTimeOpen(o => !o); setRedemptionOpen(false); if (timeOpen) setMonthOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: 'white', border: '1.5px solid #e8e0e0', borderRadius: '24px', cursor: 'pointer' }}
            >
              <IconCalendar width={18} height={18} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a' }}>{timeButtonLabel}</span>
              <ChevronDown />
            </button>

            {timeOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'white', borderRadius: '14px', boxShadow: '0 8px 28px rgba(0,0,0,0.12)', minWidth: '200px', zIndex: 50, padding: '6px 0', border: '1px solid #f0e8e8' }}>
                {TIME_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.key}
                    onClick={() => handleTimeOptionClick(opt)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: '10px', width: '100%', padding: '10px 16px',
                      background: selectedTime.key === opt.key ? '#fdf0f0' : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '14px',
                      fontWeight: selectedTime.key === opt.key ? '600' : '400',
                      color: selectedTime.key === opt.key ? '#D4001D' : '#1a1a1a',
                      borderBottom: i < TIME_OPTIONS.length - 1 ? '1px solid #f5f0f0' : 'none',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <opt.icon width={16} height={16} />
                      {opt.label}
                    </span>
                    {opt.key === 'select_month' && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none"
                        style={{ transform: monthOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                        <path d="M3 5l4 4 4-4" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                ))}

                {monthOpen && (
                  <div style={{ borderTop: '1px solid #f5f0f0', maxHeight: '220px', overflowY: 'auto', padding: '4px 0' }}>
                    {RECENT_MONTHS.map(month => (
                      <button
                        key={month.key}
                        onClick={() => handleMonthSelect(month)}
                        style={{
                          display: 'flex', alignItems: 'center', width: '100%',
                          padding: '9px 20px', border: 'none', cursor: 'pointer', textAlign: 'left',
                          fontSize: '13px',
                          background: selectedMonth?.key === month.key ? '#fdf0f0' : 'transparent',
                          fontWeight: selectedMonth?.key === month.key ? '600' : '400',
                          color: selectedMonth?.key === month.key ? '#D4001D' : '#444',
                        }}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Daily / Cumulative toggle ── */}
        {canToggle && (
          <div style={{ display: 'flex', width: 'fit-content', background: 'white', borderRadius: 20, padding: 3, border: '1.5px solid #e8e0e0', marginBottom: 14 }}>
            {[{ key: 'daily', label: 'Daily' }, { key: 'cumulative', label: 'Cumulative' }].map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => setViewMode(m.key)}
                style={{
                  padding: '5px 14px', borderRadius: 16, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600,
                  background: effectiveMode === m.key ? '#D4001D' : 'transparent',
                  color: effectiveMode === m.key ? '#fff' : '#555',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {/* ── Legend ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
          {isStatsMetric ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#D4001D' }} />
              <span style={{ fontSize: '12px', color: '#555' }}>{selectedRedemption.label}</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#D4001D' }} />
                <span style={{ fontSize: '12px', color: '#555' }}>Amount off</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#F5D97A' }} />
                <span style={{ fontSize: '12px', color: '#555' }}>Points Back</span>
              </div>
            </>
          )}
        </div>

        {/* ── Stat pills ── */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
          {(isStatsMetric
            ? [{ label: selectedRedemption.label, value: statsTotal }]
            : [
                { label: 'Amount Off',  value: data.amount_off_coupons_count  || 0 },
                { label: 'Points Back', value: data.points_back_coupons_count || 0 },
                { label: 'Total',       value: total },
              ]
          ).map(({ label, value }) => (
            <div key={label} style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
              background: 'white',
              borderRadius: '10px',
              padding: '6px 8px',
              border: '1px solid #ede8e8',
              overflow: 'hidden',
            }}>
              <span style={{
                fontSize: 'clamp(9px, 0.7vw, 11px)',
                color: '#888',
                fontWeight: '400',
                whiteSpace: 'nowrap',
                flexShrink: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>{label}</span>
              <span style={{
                fontSize: 'clamp(10px, 0.8vw, 14px)',
                fontWeight: '700',
                color: '#1a1a1a',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>{value}</span>
            </div>
          ))}
        </div>

        {/* ── Chart ── */}
        {fetched && chartData.length > 0 ? (
          <div style={{ background: 'white', borderRadius: '16px', padding: '16px 8px 8px 0' }}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4001D" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#D4001D" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F5C97A" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F5C97A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#ececec" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#aaa', fontWeight: '500' }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={40} />
                <YAxis tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${parseFloat((v / 1000).toFixed(1))}k` : v} domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} width={36} />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#eee', strokeWidth: 1 }} />
                {isStatsMetric ? (
                  <Area type="monotone" dataKey="value" name={selectedRedemption.label} stroke="#D4001D" strokeWidth={2.5} fill="url(#gradRed)" dot={false} activeDot={{ r: 6, fill: '#3B0A1F', strokeWidth: 0 }} />
                ) : (
                  <>
                    <Area type="monotone" dataKey="amountOff"  name="Amount Off"  stroke="#D4001D" strokeWidth={2.5} fill="url(#gradRed)"  dot={false} activeDot={{ r: 6, fill: '#3B0A1F', strokeWidth: 0 }} />
                    <Area type="monotone" dataKey="pointsBack" name="Points Back" stroke="#F5C97A" strokeWidth={2.5} fill="url(#gradGold)" dot={false} activeDot={{ r: 6, fill: '#F5C97A', strokeWidth: 0 }} />
                  </>
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '13px', gap: '8px' }}>
            <Spinner show={fetching} />
            {!fetching && 'No redemption data available'}
          </div>
        )}

        <p style={{ margin: '10px 4px 0', fontSize: '11px', color: '#999' }}>
          Today's data isn't shown — it will be available tomorrow.
        </p>
      </div>

      {/* ── Date Range Picker modal ── */}
      {showPicker && (
        <DateRangePicker
          onClose={() => {
            setShowPicker(false);
            if (!appliedRange) setSelectedTime(TIME_OPTIONS[0]);
          }}
          onApply={({ startDate, endDate }) => {
            const range = { startDate: toLocalIso(startDate), endDate: toLocalIso(endDate) };
            setAppliedRange(range);
            setSelectedMonth(null);
            setShowPicker(false);
            dispatchFilter({ time: TIME_OPTIONS.find(o => o.key === 'date_range'), range, month: null });
          }}
        />
      )}
    </>
  );
};

RedemptionsChart.propTypes = {
  data:           PropTypes.object.isRequired,
  fetching:       PropTypes.bool,
  fetched:        PropTypes.bool,
  onFilterChange: PropTypes.func,
  defaultRedemptionKey: PropTypes.string,
};

export default RedemptionsChart;